import { Fragment, ReactNode, useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import {
    CheckCircleIcon,
    XMarkIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';

type NotificationProps = {
    title: string;
    message?: string;
    link?: ReactNode;
    className?: string | object;
    onDismiss?: () => void;
    persist?: boolean;
    duration?: number;
} & Variant;

type Variant = {
    variant?: 'success' | 'danger' | 'warning' | 'info' | 'default';
};

export default function Notification({
    title,
    message,
    link,
    onDismiss,
    duration = 2000,
    persist = false,
    variant = 'default',
    ...rest
}: NotificationProps) {
    const [show, setShow] = useState(true);
    const [timeoutId, setTimeoutId] = useState<number | null>(null);

    const durationHandler = () => {
        if (duration && !persist) {
            return setTimeout(() => {
                setShow(false);
            }, duration);
        }
        return null;
    };

    useEffect(() => {
        setTimeoutId(durationHandler());
    }, []);

    return (
        <Transition
            show={show}
            as={Fragment}
            enter="alli-transform alli-ease-out alli-duration-300 alli-transition"
            enterFrom="alli-translate-y-2 alli-opacity-0 sm:alli-translate-y-0 sm:alli-translate-x-2"
            enterTo="alli-translate-y-0 alli-opacity-100 sm:alli-translate-x-0"
            leave="alli-transition alli-ease-in alli-duration- "
            leaveFrom="alli-opacity-100"
            leaveTo="alli-opacity-0">
            <div
                {...rest}
                onMouseEnter={() => {
                    if (timeoutId) clearTimeout(timeoutId);
                }}
                onMouseLeave={() => {
                    if (!persist) setTimeoutId(durationHandler());
                }}
                className="alli-pointer-events-auto alli-w-full alli-min-w-96 alli-max-w-sm alli-cursor-pointer alli-overflow-hidden alli-rounded-lg alli-bg-white alli-shadow-lg alli-ring-1 alli-ring-black alli-ring-opacity-5">
                <div className="alli-p-4">
                    <div className="alli-flex alli-items-start">
                        <div className="alli-flex-shrink-0">
                            <QualityIcon variant={variant} />
                        </div>
                        <div className="alli-ml-3 alli-w-0 alli-flex-1 alli-pt-0.5">
                            <p className="alli-text-sm alli-font-medium alli-text-blue-gray-700">
                                {title}
                            </p>
                            {message && (
                                <p className="alli-mt-1 alli-text-sm alli-text-gray-750">
                                    {message}
                                </p>
                            )}
                        </div>
                        {link && (
                            <div className="alli-text-sm alli-font-medium alli-text-blue-600">
                                {link}
                            </div>
                        )}
                        <div className="alli-ml-4 alli-flex alli-flex-shrink-0">
                            <button
                                type="button"
                                className="alli-inline-flex alli-rounded-md alli-bg-white alli-text-gray-400 hover:alli-text-gray-500 focus:alli-outline-none focus:alli-ring-2 focus:alli-ring-blue-600 focus:alli-ring-offset-2"
                                onClick={() => {
                                    onDismiss && onDismiss();
                                    setShow(false);
                                }}>
                                <span className="alli-sr-only">Close</span>
                                <XMarkIcon
                                    className="alli-h-5 alli-w-5 alli-text-gray-700"
                                    aria-hidden="true"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    );
}

export function NotificationContainer({ children }: { children: ReactNode }) {
    return (
        <>
            <div
                aria-live="assertive"
                className="alli-pointer-events-none alli-fixed alli-inset-0 alli-flex alli-items-end alli-px-4 alli-py-6 sm:alli-items-start sm:alli-p-6">
                <div className="alli-flex alli-w-full alli-flex-col alli-items-center alli-space-y-4 sm:alli-items-end">
                    {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
                    {children}
                </div>
            </div>
        </>
    );
}

function QualityIcon({ variant }: Variant) {
    switch (variant) {
        case 'success':
            return (
                <CheckCircleIcon
                    data-testid="quality-icon"
                    className="alli-h-6 alli-w-6 alli-text-green-700"
                    aria-hidden="true"
                />
            );
            break;
        case 'danger':
            return (
                <XCircleIcon
                    data-testid="quality-icon"
                    className="alli-h-6 alli-w-6 alli-text-red-600"
                    aria-hidden="true"
                />
            );
        case 'info':
            return (
                <InformationCircleIcon
                    data-testid="quality-icon"
                    className="alli-h-6 alli-w-6 alli-text-blue-600"
                    aria-hidden="true"
                />
            );
        case 'warning':
            return (
                <ExclamationTriangleIcon
                    data-testid="quality-icon"
                    className="alli-h-6 alli-w-6 alli-text-orange-600"
                    aria-hidden="true"
                />
            );
        default:
            return null;
    }
}
